import { Options } from '../index';
import { ICampaignData } from '../interface/ICampaignData';
import { trackerEventNames, urlParameter } from '../library/Constants';
import Cookie, { CookieKey, CookieLifetime } from '../library/Cookie';
import { getLandingUrl, getUrlParameter, utils } from '../utils/Utils';
import Session from '../valueobject/Session';
import CampaignService from './CampaignService';
import MarketingService from './MarketingService';
import OptimizelyService from './OptimizelyService';
import { IMarketingData } from '../interface/IMarketingData';
import { encodeQuestionnaireSteps } from '../library/QuestionnaireCodes';
import Tracker from '../library/Tracker';
import GtmLoadMonitor, { GtmSource } from '../library/GtmLoadMonitor';
import { mapTrackingDimensions, TRACKING_ERRORS_PROPERTY, TrackingDimensions } from '../library/TrackingDimensions';

declare let window: any;

const LAYER_NAME = 'dataLayer';
const QUESTIONNAIRE_CODES_LOCALE = 'de_DE';

export const CAMPAIGN_SERVICE_ERROR = 'act:campaign_service';

/**
 * CJT-15943: tracker block lists stop GTM by host and URL shape (googletagmanager.com, /gtm.js, ?id=GTM-), and with it
 * every tag, PostHog included. The DE container is also served by our flow.<apex> CloudFront proxy under a neutral path,
 * as a copy of Google's gtm.js that is checked to carry German geo (martech.website.lp.infrastructure, posthog-proxy/).
 * If the proxy fails to load, Google's URL is loaded instead: today's behaviour, never a second GTM.
 */
const GTM_PROXY_CONTAINER = 'GTM-WRBTKB9';
const GTM_PROXY_LOCALE = 'de_de';
const GTM_PROXY_PATH = '/tm-asset/site.jsm';
// Apexes with a flow. host on the proxy; any other host loads from the first one.
export const GTM_PROXY_APEXES = [
  'hoeren-heute.de',
  'audibene.de',
  'audibene-hoergeraete.de',
  'audibene-magazin.de',
  'xn--hrgerte-9wa1n.com',
  'hoergeraeteexperten.de'
];
// The rollout switch. false: proxy only after ?gtm_src=flow (opt-in test). true: proxy for all de_DE pages.
export const GTM_PROXY_ROLLOUT = { enabledByDefault: false };
// ?gtm_src=flow|google picks the source and remembers it in a cookie for later pages; ?gtm_src=reset forgets it.
export const GTM_SOURCE_PARAMETER = 'gtm_src';
const GTM_SOURCE_RESET = 'reset';
// GTM Preview / environments load a different gtm.js from Google; the proxy only serves the live container.
const GTM_PREVIEW_PARAMETERS = ['gtm_debug', 'gtm_auth', 'gtm_preview'];

const isGtmSource = (value: any): value is GtmSource => value === 'flow' || value === 'google';

/**
 * The visitor's explicit choice of GTM source: the URL parameter (stored or cleared in the cookie), else the cookie.
 */
export const getGtmSourceOverride = (): GtmSource | null => {
  const parameter = (getUrlParameter(GTM_SOURCE_PARAMETER) || '').toLowerCase();

  if (isGtmSource(parameter)) {
    Cookie.set(CookieKey.GTM_SOURCE, parameter, CookieLifetime.THIRTY_DAYS);
    return parameter;
  }

  if (parameter === GTM_SOURCE_RESET) {
    Cookie.unset(CookieKey.GTM_SOURCE);
    return null;
  }

  const stored = Cookie.get(CookieKey.GTM_SOURCE);

  return isGtmSource(stored) ? stored : null;
};

const isGtmPreview = (): boolean => GTM_PREVIEW_PARAMETERS.some((name) => getUrlParameter(name));

export const getGtmProxyHost = (hostname: string): string => {
  const host = (hostname || '').toLowerCase();
  const apex = GTM_PROXY_APEXES.find((domain) => host === domain || host.endsWith(`.${domain}`));

  return `flow.${apex || GTM_PROXY_APEXES[0]}`;
};

class GtmService {
  private session: Session;

  private readonly options: Options;

  private campaignService: CampaignService;

  private marketingService: MarketingService;

  private optimizelyService: OptimizelyService;

  private tracker: Tracker;

  private reportedUnmapped: { [key: string]: boolean } = {};

  constructor(
    session: Session,
    marketingService: MarketingService,
    campaignService: CampaignService,
    optimizelyService: OptimizelyService,
    options: Options,
    tracker?: Tracker
  ) {
    this.session = session;
    this.marketingService = marketingService;
    this.campaignService = campaignService;
    this.optimizelyService = optimizelyService;
    this.options = options;
    this.tracker = tracker;
  }

  public async init(): Promise<any> {
    const { gtmId } = this.options;

    let marketingData: IMarketingData;
    let campaignData: ICampaignData;

    try {
      marketingData = await this.marketingService.getData();
    } catch {
      window.TrackJS && window.TrackJS.console.log('SL:[GtmService] Marketing data request failed');
    }

    try {
      campaignData = await this.campaignService.getData();
    } catch {
      window.TrackJS && window.TrackJS.console.log('SL:[GtmService] Campaign data request failed');
    }

    const data = this.prepareDataForGTM(marketingData, campaignData);

    this.unshift(data);

    function gtag(consent: string, behavior: string, data: any) {
      window[LAYER_NAME].push(arguments);
    }

    if (gtmId) {
      const locale = this.options.locale?.toLocaleLowerCase();
      // GDPR = General Data Protection Regulation
      const gdprLocales = ['de_de', 'nl_nl', 'fr_fr', 'de_ch'];
      const isCountryWithGDPR = gdprLocales.includes(locale);

      if (isCountryWithGDPR) {
        gtag('consent', 'default', {
          ad_user_data: 'denied',
          ad_personalization: 'denied',
          ad_storage: 'denied',
          analytics_storage: 'denied',
          wait_for_update: 2000
        });
      }

      this.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
      });
    }

    this.placeScriptTag();
  }

  /**
   * https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation/type
   * @param device
   */
  private getNetworkType(device = ''): string {
    const isMobile = device.toLowerCase() === 'mobile';

    const connection =
      window.navigator.connection || window.navigator.mozConnection || window.navigator.webkitConnection || {};
    const { type = null } = connection;

    return isMobile ? type : null;
  }

  /**
   * Dimensions mapped from the first-touch landing URL (the one Salesforce stores as Landing_URL__c),
   * so every page of the session, questionnaire included, carries the attribution of the lead.
   *
   * Plus the ACT: the campaign service's, or, when that request failed (it then returns a default ACT),
   * the ACT sessionLayer sent it (data-act, URL, cookie). The failure is flagged in tracking_errors.
   */
  private getTrackingDimensions(campaign: ICampaignData): TrackingDimensions {
    try {
      const tracking = mapTrackingDimensions(String(getLandingUrl() || this.session.getUrl()));
      const failed = this.campaignService.hasFailed() || !campaign.act;
      const act = failed ? this.options.act : campaign.act;

      if (act && typeof act === 'string') {
        tracking.act = act;
      }

      if (failed) {
        tracking[TRACKING_ERRORS_PROPERTY] = ((tracking[TRACKING_ERRORS_PROPERTY] as string[]) || []).concat(
          CAMPAIGN_SERVICE_ERROR
        );
      }

      return tracking;
    } catch (err) {
      window.TrackJS && window.TrackJS.console.log('SL:[GtmService] Tracking dimensions mapping failed');
      return {};
    }
  }

  private prepareDataForGTM(marketingData: IMarketingData, campaign: ICampaignData = {}) {
    const { locale } = this.options;
    const device = marketingData?.browser?.device;

    const gtmData = {
      page: {
        referrer: Cookie.get(CookieKey.REFERRER),
        url: this.session.getUrl(),
        varid: [
          ...utils.getMatchingUrlParameters(urlParameter.VARIATION_ID),
          ...this.optimizelyService.getVariationIds()
        ].join(',')
      },
      user: {
        gid: this.session.getGid(),
        leadId: this.session.getLeadId(),
        visitorId: this.session.getVisitorId()
      },
      networkType: this.getNetworkType(device),
      ...(marketingData || {}),
      campaign,
      tracking: this.getTrackingDimensions(campaign)
    };

    // TODO: do we need overwriting the locale from marketing?
    gtmData.site = {
      ...((marketingData && marketingData.site) || {}),
      locale
    };

    return gtmData;
  }

  public placeScriptTag(): void {
    const { gtmId } = this.options;

    if (!gtmId) {
      return;
    }

    const source = this.getGtmSource(gtmId);
    // GTM Preview never sets the container's bootstrap, so its page loads would all look broken: not reported.
    const monitor =
      this.isProxyContainer(gtmId) && this.tracker && !isGtmPreview() ? new GtmLoadMonitor(this.tracker, gtmId) : null;
    let fellBack = false;

    monitor?.start(source);

    this.insertScript(this.getSourceUrl(gtmId, source), () => {
      // One shared guard: only the proxy script falls back, and only once. Hangs are not retried.
      if (source === 'flow' && !fellBack) {
        fellBack = true;
        monitor?.markFallback();
        this.insertScript(this.getSourceUrl(gtmId, 'google'));
      }
    });
  }

  private insertScript(src: string, onError?: () => void): void {
    const firstScript = document.querySelector('script');
    const script: HTMLScriptElement = document.createElement('script');

    script.async = true;
    script.setAttribute('data-usercentrics', 'Google Tag Manager');
    if (onError) {
      script.onerror = onError;
    }
    script.src = src;

    // element can be null
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    }
  }

  public getScriptSrc(gtmId: string): string {
    return this.getSourceUrl(gtmId, this.getGtmSource(gtmId));
  }

  private getSourceUrl(gtmId: string, source: GtmSource): string {
    if (source === 'flow') {
      return `https://${getGtmProxyHost(window.location.hostname)}${GTM_PROXY_PATH}`;
    }

    return `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  }

  private isProxyContainer(gtmId: string): boolean {
    return gtmId === GTM_PROXY_CONTAINER && this.options.locale?.toLowerCase() === GTM_PROXY_LOCALE;
  }

  private getGtmSource(gtmId: string): GtmSource {
    if (!this.isProxyContainer(gtmId)) {
      return 'google';
    }

    // Read first, so ?gtm_src=… is remembered (or reset) even on a Preview page.
    const override = getGtmSourceOverride();

    if (isGtmPreview()) {
      return 'google';
    }

    if (override) {
      return override;
    }

    return GTM_PROXY_ROLLOUT.enabledByDefault ? 'flow' : 'google';
  }

  public unshift(payload: any): void {
    window[LAYER_NAME].unshift(payload);
  }

  private process(payload: any): void {
    const { data } = payload.questionnaire || {};

    if (data && data.Email) {
      data.emailHashed = window.md5(data.Email.trim());
    }

    // The code table only covers the DE forms; other markets push the questionnaire unchanged.
    if (payload.questionnaire && this.session.getLocale() === QUESTIONNAIRE_CODES_LOCALE) {
      try {
        encodeQuestionnaireSteps(payload.questionnaire, (detail) => this.reportUnmapped(detail));
      } catch (err) {
        window.TrackJS && window.TrackJS.console.log('SL:[GtmService] Questionnaire coding failed');
      }
    }

    return payload;
  }

  /**
   * A field or answer missing from the code table (a new question or option in a form). Logged once per page so
   * New Relic can alert on it; the table in questionnaireCodesMapping.ts needs a new entry.
   */
  private reportUnmapped(detail: { field: string; value?: string }): void {
    const key = `${detail.field}|${detail.value || ''}`;

    if (this.reportedUnmapped[key] || !this.tracker) {
      return;
    }

    this.reportedUnmapped[key] = true;
    this.tracker.track(trackerEventNames.QUESTIONNAIRE_CODE_UNMAPPED, detail);
  }

  public push(payload: any): void {
    if (!payload) {
      return;
    }

    const data = this.process(payload);
    window[LAYER_NAME].push(data);
  }
}

window[LAYER_NAME] = window[LAYER_NAME] || [];

export default GtmService;
